import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    brandOpsAccessUrl,
    brandOpsCalendarApproveUrl,
    brandOpsCalendarPublishUrl,
    brandOpsCalendarItemUrl,
    brandOpsCalendarUrl,
    brandOpsContractDispatchUrl,
    brandOpsContractRenewUrl,
    brandOpsContractPdfUrl,
    brandOpsContractSignUrl,
    brandOpsContractUrl,
    brandOpsContractsMineUrl,
    brandOpsContractsUrl,
    brandOpsAiPlaygroundUrl,
    brandOpsContractEscrowLockUrl,
    brandOpsContractEscrowPayoutUrl,
    brandOpsContractEscrowReleaseUrl,
    brandOpsContractStrikesUrl,
    brandOpsContractFreezeUrl,
    brandOpsContractVoidUrl,
    brandOpsContractApproveUrl,
    brandOpsContractReviewUrl,
    brandOpsContractClarificationsUrl,
    brandOpsContractClarificationReplyUrl,
    brandOpsInboxActivityUrl,
    brandOpsInboxAutomationsUrl,
    brandOpsInboxAutomationToggleUrl,
    brandOpsInboxConversationsUrl,
    brandOpsInboxStatusUrl,
    brandOpsInboxConversationMessagesUrl,
    brandOpsInboxSendMessageUrl,
    brandOpsInboxUrl,
    brandOpsKnowledgeDocUrl,
    brandOpsKnowledgeUrl,
    brandOpsSettingsUrl,
    brandOpsHomeStatsUrl,
    brandOpsUgcAssetsUrl,
    brandOpsTeamAcceptUrl,
    brandOpsTeamMemberUrl,
    brandOpsTeamMemberResendUrl,
    brandOpsTeamMineUrl,
    brandOpsTeamUrl,
    brandUrl,
} from '../../configurations/api/url';
import { saveBlob } from '../../utils/downloadFile';
import { SentryCapture } from '../../analytics/Sentry';
import { setBrandDashboardActiveBrand } from '../buzzbridge/actions';
import { brandsSelector } from '../buzzbridge/selectors';
import { httpRequest } from '../types';
import { settle } from '../sandbox/sagaHelpers';
import { destroyOneCache, setCache } from '../cache/action';
import { CACHE_TYPE, type CacheValue } from '../cache/types';
import { getCacheByType, getCacheDuration, isUseCacheEnabled } from '../cache/saga';
import {
    acceptBrandOpsInvite,
    acceptBrandOpsInviteFail,
    acceptBrandOpsInviteSuccess,
    approveBrandOpsCalendarItem,
    approveBrandOpsCalendarItemFail,
    approveBrandOpsCalendarItemSuccess,
    publishBrandOpsCalendarItem,
    publishBrandOpsCalendarItemFail,
    publishBrandOpsCalendarItemSuccess,
    downloadBrandOpsContractPdf,
    downloadBrandOpsContractPdfFail,
    downloadBrandOpsContractPdfSuccess,
    fetchBrandOpsKnowledge,
    fetchBrandOpsKnowledgeFail,
    fetchBrandOpsKnowledgeSuccess,
    addBrandOpsKnowledge,
    addBrandOpsKnowledgeFail,
    addBrandOpsKnowledgeSuccess,
    removeBrandOpsKnowledge,
    removeBrandOpsKnowledgeFail,
    removeBrandOpsKnowledgeSuccess,
    runBrandOpsAiPlayground,
    runBrandOpsAiPlaygroundFail,
    runBrandOpsAiPlaygroundSuccess,
    fetchBrandOpsInbox,
    fetchBrandOpsInboxFail,
    fetchBrandOpsInboxSuccess,
    fetchBrandOpsInboxStatus,
    fetchBrandOpsInboxStatusFail,
    fetchBrandOpsInboxStatusSuccess,
    lockBrandOpsEscrow,
    lockBrandOpsEscrowFail,
    lockBrandOpsEscrowSuccess,
    payoutBrandOpsEscrow,
    payoutBrandOpsEscrowFail,
    payoutBrandOpsEscrowSuccess,
    releaseBrandOpsEscrow,
    releaseBrandOpsEscrowFail,
    releaseBrandOpsEscrowSuccess,
    addBrandOpsStrike,
    addBrandOpsStrikeFail,
    addBrandOpsStrikeSuccess,
    fetchBrandOpsConversations,
    fetchBrandOpsConversationsFail,
    fetchBrandOpsConversationsSuccess,
    fetchBrandOpsConversationMessages,
    fetchBrandOpsConversationMessagesFail,
    fetchBrandOpsConversationMessagesSuccess,
    sendBrandOpsInboxMessage,
    sendBrandOpsInboxMessageFail,
    sendBrandOpsInboxMessageSuccess,
    fetchBrandOpsUgcAssets,
    fetchBrandOpsUgcAssetsFail,
    fetchBrandOpsUgcAssetsSuccess,
    fetchMyBrandMemberships,
    fetchMyBrandMembershipsSuccess,
    fetchMyBrandMembershipsFail,
    fetchMyBrandOpsContract,
    fetchMyBrandOpsContractSuccess,
    fetchMyBrandOpsContractFail,
    resendBrandOpsTeamInvite,
    resendBrandOpsTeamInviteSuccess,
    resendBrandOpsTeamInviteFail,
    renewBrandOpsContract,
    renewBrandOpsContractSuccess,
    renewBrandOpsContractFail,
    freezeBrandOpsContract,
    freezeBrandOpsContractSuccess,
    freezeBrandOpsContractFail,
    voidBrandOpsContract,
    voidBrandOpsContractSuccess,
    voidBrandOpsContractFail,
    fetchBrandOpsAutomations,
    fetchBrandOpsAutomationsSuccess,
    fetchBrandOpsAutomationsFail,
    upsertBrandOpsAutomation,
    upsertBrandOpsAutomationSuccess,
    upsertBrandOpsAutomationFail,
    toggleBrandOpsAutomation,
    toggleBrandOpsAutomationSuccess,
    toggleBrandOpsAutomationFail,
    approveBrandOpsContract,
    approveBrandOpsContractSuccess,
    approveBrandOpsContractFail,
    getContractForReview,
    getContractForReviewSuccess,
    getContractForReviewFail,
    fetchContractClarifications,
    fetchContractClarificationsSuccess,
    fetchContractClarificationsFail,
    replyToContractClarification,
    replyToContractClarificationSuccess,
    replyToContractClarificationFail,
    createBrandOpsCalendarItem,
    createBrandOpsCalendarItemFail,
    createBrandOpsCalendarItemSuccess,
    createBrandOpsContract,
    createBrandOpsContractFail,
    createBrandOpsContractSuccess,
    dispatchBrandOpsContract,
    dispatchBrandOpsContractFail,
    dispatchBrandOpsContractSuccess,
    fetchBrandOpsAccess,
    fetchBrandOpsHomeStats,
    fetchBrandOpsHomeStatsSuccess,
    fetchBrandOpsAccessFail,
    fetchBrandOpsAccessSuccess,
    fetchBrandOpsCalendar,
    fetchBrandOpsCalendarFail,
    fetchBrandOpsCalendarSuccess,
    fetchBrandOpsContracts,
    fetchBrandOpsContractsFail,
    fetchBrandOpsContractsSuccess,
    fetchBrandOpsSettings,
    fetchBrandOpsSettingsFail,
    fetchBrandOpsSettingsSuccess,
    fetchBrandOpsTeam,
    fetchBrandOpsTeamFail,
    fetchBrandOpsTeamSuccess,
    getBrandOpsContract,
    getBrandOpsContractFail,
    getBrandOpsContractSuccess,
    inviteBrandOpsTeam,
    inviteBrandOpsTeamFail,
    inviteBrandOpsTeamSuccess,
    removeBrandOpsTeamMember,
    removeBrandOpsTeamMemberFail,
    removeBrandOpsTeamMemberSuccess,
    signBrandOpsContract,
    signBrandOpsContractFail,
    signBrandOpsContractSuccess,
    updateBrandOpsCalendarItem,
    updateBrandOpsCalendarItemFail,
    updateBrandOpsCalendarItemSuccess,
    updateBrandOpsSettings,
    updateBrandOpsSettingsFail,
    updateBrandOpsSettingsSuccess,
    updateBrandOpsTeamMember,
    updateBrandOpsTeamMemberFail,
    updateBrandOpsTeamMemberSuccess,
} from './actions';

function unwrapData<T>(response: any): T {
    const body = response?.data;
    if (body?.data !== undefined) return body.data as T;
    if (body !== undefined) return body as T;
    return response as T;
}

function asList<T>(data: any): T[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.team)) return data.team;
    if (Array.isArray(data?.contracts)) return data.contracts;
    if (Array.isArray(data?.calendar)) return data.calendar;
    return [];
}

function brandOpsHeaders(brandId: string): Record<string, string> {
    return { 'X-Brand-Id': brandId };
}

function brandOpsApi(brandId: string, url: string, method: string, body: any = null) {
    return call(
        api,
        url,
        method,
        body,
        2,
        null,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        brandOpsHeaders(brandId),
    );
}

function* cachedListFetch({
    cacheType,
    force,
    brandId,
    url,
    success,
    fail,
    resolve,
    reject,
}: {
    cacheType: CACHE_TYPE;
    force?: boolean;
    brandId: string;
    url: string;
    success: (list: any[]) => any;
    fail: (err: any) => any;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(cacheType);
    const cacheKeyMatch = cache?.key && String(cache.key).startsWith(`${brandId}:`);
    if (cache && cacheKeyMatch) {
        initialResult = cache.value;
    }

    if (
        !force &&
        !!initialResult &&
        defaultUseCache &&
        ((Date.now() - Number(String(cache.key).split(':')[1])) as unknown as number) < defaultCacheDuration
    ) {
        yield put(success(asList(initialResult)));
        settle(resolve, reject, initialResult);
        return;
    }

    try {
        const response = yield brandOpsApi(brandId, url, httpRequest.GET);
        const data = unwrapData(response);
        const list = asList(data);
        yield put(success(list));
        yield put(
            setCache({
                key: `${brandId}:${Date.now()}`,
                value: list,
                type: cacheType,
                isUnique: true,
            }),
        );
        settle(resolve, reject, list);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchAccessSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsAccessUrl, httpRequest.GET);
        const data = unwrapData(response);
        yield put(fetchBrandOpsAccessSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsAccessFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchTeamSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload || {};
    try {
        const response = yield brandOpsApi(brandId, brandOpsTeamUrl, httpRequest.GET);
        // api() already strips the axios wrapper: response = { status, data: { members, total } }
        const body = response?.data ?? response;
        const list: any[] = Array.isArray(body)
            ? body
            : Array.isArray(body?.members)
            ? body.members
            : Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.data?.members)
            ? body.data.members
            : [];
        yield put(fetchBrandOpsTeamSuccess(list as any));
        settle(resolve, reject, list);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsTeamFail(error));
        settle(resolve, reject, error, true);
    }
}

function* inviteTeamSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, email, role, userId, contract } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsTeamUrl, httpRequest.POST, {
            email,
            role,
            user_id: userId,
            contract,
        });
        const data = unwrapData(response);
        yield put(inviteBrandOpsTeamSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_TEAM }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(inviteBrandOpsTeamFail(error));
        settle(resolve, reject, error, true);
    }
}

function* updateTeamMemberSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, memberId, role, status } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsTeamMemberUrl(memberId), httpRequest.PATCH, {
            role,
            status,
        });
        const data = unwrapData(response);
        yield put(updateBrandOpsTeamMemberSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_TEAM }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateBrandOpsTeamMemberFail(error));
        settle(resolve, reject, error, true);
    }
}

function* removeTeamMemberSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, memberId } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsTeamMemberUrl(memberId), httpRequest.DELETE, {});
        yield put(removeBrandOpsTeamMemberSuccess(memberId));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_TEAM }));
        settle(resolve, reject, memberId);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(removeBrandOpsTeamMemberFail(error));
        settle(resolve, reject, error, true);
    }
}

function* acceptInviteSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const brandId = payload.brandId || '';
    try {
        const response = yield brandOpsApi(brandId || 'global', brandOpsTeamAcceptUrl, httpRequest.POST, {
            token: payload.token,
            id: payload.id,
        });
        const data = unwrapData(response) as any;
        yield put(acceptBrandOpsInviteSuccess(data));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_TEAM }));

        // After accepting, load the brand and set it as active so the invited
        // user lands in the correct brand context without seeing "Select a brand".
        if (data?.brand_id) {
            try {
                const brandResponse: any = yield call(
                    api,
                    `${brandUrl}/${data.brand_id}`,
                    httpRequest.GET,
                    {},
                    0,
                    0,
                    false,
                );
                const brand = brandResponse?.data?.data;
                if (brand) yield put(setBrandDashboardActiveBrand(brand));
            } catch (_) {
                // Non-critical — invite already accepted; brand context will
                // resolve when the user selects manually.
            }
        }

        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(acceptBrandOpsInviteFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchContractsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    yield* cachedListFetch({
        cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS,
        force: payload?.force,
        brandId: payload.brandId,
        url: brandOpsContractsUrl,
        success: fetchBrandOpsContractsSuccess,
        fail: fetchBrandOpsContractsFail,
        resolve,
        reject,
    });
}

function* createContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractsUrl, httpRequest.POST, body);
        const data = unwrapData(response);
        yield put(createBrandOpsContractSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* getContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractUrl(contractId), httpRequest.GET);
        const data = unwrapData(response);
        yield put(getBrandOpsContractSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* dispatchContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractDispatchUrl(contractId), httpRequest.POST, {});
        const data = unwrapData(response);
        yield put(dispatchBrandOpsContractSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(dispatchBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* signContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, typed_name, signature_data_url, affirmed } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractSignUrl(contractId), httpRequest.POST, {
            typed_name,
            signature_data_url,
            affirmed,
        });
        const data = unwrapData(response);
        yield put(signBrandOpsContractSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(signBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchCalendarSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    yield* cachedListFetch({
        cacheType: CACHE_TYPE.BRAND_OPS_CALENDAR,
        force: payload?.force,
        brandId: payload.brandId,
        url: brandOpsCalendarUrl,
        success: fetchBrandOpsCalendarSuccess,
        fail: fetchBrandOpsCalendarFail,
        resolve,
        reject,
    });
}

function* createCalendarSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsCalendarUrl, httpRequest.POST, body);
        const data = unwrapData(response);
        yield put(createBrandOpsCalendarItemSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CALENDAR }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(createBrandOpsCalendarItemFail(error));
        settle(resolve, reject, error, true);
    }
}

function* updateCalendarSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, itemId, body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsCalendarItemUrl(itemId), httpRequest.PATCH, body);
        const data = unwrapData(response);
        yield put(updateBrandOpsCalendarItemSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CALENDAR }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateBrandOpsCalendarItemFail(error));
        settle(resolve, reject, error, true);
    }
}

function* approveCalendarSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, itemId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsCalendarApproveUrl(itemId), httpRequest.POST, {});
        const data = unwrapData(response);
        yield put(approveBrandOpsCalendarItemSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CALENDAR }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(approveBrandOpsCalendarItemFail(error));
        settle(resolve, reject, error, true);
    }
}

function* publishCalendarSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, itemId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsCalendarPublishUrl(itemId), httpRequest.POST, {});
        const data = unwrapData(response);
        yield put(publishBrandOpsCalendarItemSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CALENDAR }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(publishBrandOpsCalendarItemFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchHomeStatsSaga({ payload }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsHomeStatsUrl, httpRequest.GET);
        const data = unwrapData(response);
        yield put(fetchBrandOpsHomeStatsSuccess(data as any));
    } catch {
        // non-critical — silently swallow
    }
}

function* fetchSettingsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsSettingsUrl, httpRequest.GET);
        const data = unwrapData(response);
        yield put(fetchBrandOpsSettingsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsSettingsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* updateSettingsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsSettingsUrl, httpRequest.PATCH, body);
        const data = unwrapData(response);
        yield put(updateBrandOpsSettingsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateBrandOpsSettingsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* downloadContractPdfSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield call(
            api,
            brandOpsContractPdfUrl(contractId),
            httpRequest.GET,
            null,
            2,
            null,
            false,
            undefined,
            undefined,
            undefined,
            'blob',
            brandOpsHeaders(brandId),
        );
        const blob: Blob = response instanceof Blob ? response : new Blob([response], { type: 'application/pdf' });
        saveBlob(blob, `brand-ops-contract-${contractId}.pdf`);
        yield put(downloadBrandOpsContractPdfSuccess());
        settle(resolve, reject, true);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(downloadBrandOpsContractPdfFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchKnowledgeSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    yield* cachedListFetch({
        cacheType: CACHE_TYPE.BRAND_OPS_KNOWLEDGE,
        force: payload?.force,
        brandId: payload.brandId,
        url: brandOpsKnowledgeUrl,
        success: fetchBrandOpsKnowledgeSuccess,
        fail: fetchBrandOpsKnowledgeFail,
        resolve,
        reject,
    });
}

function* addKnowledgeSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsKnowledgeUrl, httpRequest.POST, body);
        const data = unwrapData(response);
        yield put(addBrandOpsKnowledgeSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_KNOWLEDGE }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(addBrandOpsKnowledgeFail(error));
        settle(resolve, reject, error, true);
    }
}

function* removeKnowledgeSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, docId } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsKnowledgeDocUrl(docId), httpRequest.DELETE, {});
        yield put(removeBrandOpsKnowledgeSuccess(docId));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_KNOWLEDGE }));
        settle(resolve, reject, docId);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(removeBrandOpsKnowledgeFail(error));
        settle(resolve, reject, error, true);
    }
}

function* aiPlaygroundSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, message, format } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsAiPlaygroundUrl, httpRequest.POST, {
            message,
            format: format || 'instagram_dm',
        });
        const data = unwrapData(response);
        yield put(runBrandOpsAiPlaygroundSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(runBrandOpsAiPlaygroundFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchInboxSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        let response;
        try {
            response = yield brandOpsApi(brandId, brandOpsInboxActivityUrl, httpRequest.GET);
        } catch {
            response = yield brandOpsApi(brandId, brandOpsInboxUrl, httpRequest.GET);
        }
        const data = asList(unwrapData(response));
        yield put(fetchBrandOpsInboxSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsInboxFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchInboxStatusSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxStatusUrl, httpRequest.GET);
        const data = unwrapData(response);
        yield put(fetchBrandOpsInboxStatusSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsInboxStatusFail(error));
        settle(resolve, reject, error, true);
    }
}

function* refreshContractAfterEscrow(brandId: string, contractId: string): Generator<any, any, any> {
    const response = yield brandOpsApi(brandId, brandOpsContractUrl(contractId), httpRequest.GET);
    const data = unwrapData(response);
    yield put(getBrandOpsContractSuccess(data as any));
    yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS }));
    return data;
}

function* lockEscrowSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, periodId } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsContractEscrowLockUrl(contractId), httpRequest.POST, {
            period_id: periodId,
        });
        const data = yield* refreshContractAfterEscrow(brandId, contractId);
        yield put(lockBrandOpsEscrowSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(lockBrandOpsEscrowFail(error));
        settle(resolve, reject, error, true);
    }
}

function* payoutEscrowSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, periodId } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsContractEscrowPayoutUrl(contractId), httpRequest.POST, {
            period_id: periodId,
        });
        const data = yield* refreshContractAfterEscrow(brandId, contractId);
        yield put(payoutBrandOpsEscrowSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(payoutBrandOpsEscrowFail(error));
        settle(resolve, reject, error, true);
    }
}

function* releaseEscrowSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, periodId } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsContractEscrowReleaseUrl(contractId), httpRequest.POST, {
            period_id: periodId,
        });
        const data = yield* refreshContractAfterEscrow(brandId, contractId);
        yield put(releaseBrandOpsEscrowSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(releaseBrandOpsEscrowFail(error));
        settle(resolve, reject, error, true);
    }
}

function* addStrikeSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, reason } = payload;
    try {
        yield brandOpsApi(brandId, brandOpsContractStrikesUrl(contractId), httpRequest.POST, { reason });
        const data = yield* refreshContractAfterEscrow(brandId, contractId);
        yield put(addBrandOpsStrikeSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(addBrandOpsStrikeFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchConversationsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxConversationsUrl, httpRequest.GET);
        const raw = unwrapData(response) as any;
        const data = asList(raw?.conversations ?? raw);
        yield put(fetchBrandOpsConversationsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsConversationsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchConversationMessagesSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, conversationId } = payload;
    try {
        const response = yield brandOpsApi(
            brandId,
            brandOpsInboxConversationMessagesUrl(conversationId),
            httpRequest.GET,
        );
        const raw = unwrapData(response) as any;
        const data = asList(raw?.messages ?? raw);
        yield put(fetchBrandOpsConversationMessagesSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsConversationMessagesFail(error));
        settle(resolve, reject, error, true);
    }
}

function* sendInboxMessageSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, recipient_id, text } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxSendMessageUrl, httpRequest.POST, {
            recipient_id,
            text,
        });
        const data = unwrapData(response);
        yield put(sendBrandOpsInboxMessageSuccess());
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(sendBrandOpsInboxMessageFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchUgcAssetsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, campaignId } = payload;
    try {
        const qs = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
        const response = yield brandOpsApi(brandId, `${brandOpsUgcAssetsUrl}${qs}`, httpRequest.GET);
        const data = asList(unwrapData(response));
        yield put(fetchBrandOpsUgcAssetsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsUgcAssetsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchMyContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractsMineUrl, httpRequest.GET);
        const data = unwrapData(response);
        yield put(fetchMyBrandOpsContractSuccess((data ?? null) as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMyBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

/** Fetches all brands the calling user is an active manager of (no brand context needed).
 *  Sets the first result as the active brand dashboard brand so non-owners land
 *  in the correct brand context without being prompted to create a brand. */
function* fetchMyMembershipsSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, brandOpsTeamMineUrl, httpRequest.GET, {}, 0, 0, false);
        // api() already unwraps to response.data, so the JSON body is `response`
        // and the memberships array is at response.data (not response.data.data)
        const data: any[] = response?.data ?? [];
        if (data.length > 0) {
            const ownedBrands: any[] = yield select(brandsSelector);
            if (ownedBrands.length === 0) {
                yield put(setBrandDashboardActiveBrand(data[0] as any));
            }
        }
        yield put(fetchMyBrandMembershipsSuccess(data as any));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchMyBrandMembershipsFail(error));
    }
}

function* resendTeamInviteSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, memberId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsTeamMemberResendUrl(memberId), httpRequest.POST, {});
        const data = unwrapData(response);
        yield put(resendBrandOpsTeamInviteSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(resendBrandOpsTeamInviteFail(error));
        settle(resolve, reject, error, true);
    }
}

function* renewContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractRenewUrl(contractId), httpRequest.POST, {});
        const data = unwrapData(response);
        yield put(renewBrandOpsContractSuccess(data as any));
        yield put(destroyOneCache({ cacheType: CACHE_TYPE.BRAND_OPS_CONTRACTS }));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(renewBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* freezeContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, reason } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractFreezeUrl(contractId), httpRequest.POST, {
            reason,
        });
        const data = unwrapData(response);
        yield put(freezeBrandOpsContractSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(freezeBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* voidContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, reason } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractVoidUrl(contractId), httpRequest.POST, { reason });
        const data = unwrapData(response);
        yield put(voidBrandOpsContractSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(voidBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchAutomationsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxAutomationsUrl, httpRequest.GET);
        const data = asList(unwrapData(response));
        yield put(fetchBrandOpsAutomationsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchBrandOpsAutomationsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* upsertAutomationSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, ...body } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxAutomationsUrl, httpRequest.POST, body);
        const data = unwrapData(response);
        yield put(upsertBrandOpsAutomationSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(upsertBrandOpsAutomationFail(error));
        settle(resolve, reject, error, true);
    }
}

function* toggleAutomationSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, automationId, enabled } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsInboxAutomationToggleUrl(automationId), httpRequest.PATCH, {
            enabled,
        });
        const data = unwrapData(response);
        yield put(toggleBrandOpsAutomationSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(toggleBrandOpsAutomationFail(error));
        settle(resolve, reject, error, true);
    }
}

function* approveContractSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractApproveUrl(contractId), httpRequest.POST);
        const data = unwrapData(response);
        yield put(approveBrandOpsContractSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(approveBrandOpsContractFail(error));
        settle(resolve, reject, error, true);
    }
}

function* getContractForReviewSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { token } = payload;
    try {
        const response = yield call(
            api,
            `${brandOpsContractReviewUrl}?token=${encodeURIComponent(token)}`,
            httpRequest.GET,
            {},
            0,
            0,
            false,
        );
        const data = unwrapData(response);
        yield put(getContractForReviewSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getContractForReviewFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchContractClarificationsSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId } = payload;
    try {
        const response = yield brandOpsApi(brandId, brandOpsContractClarificationsUrl(contractId), httpRequest.GET);
        const data = asList(unwrapData(response));
        yield put(fetchContractClarificationsSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchContractClarificationsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* replyToContractClarificationSaga({ payload, resolve, reject }: any): Generator<any, void, any> {
    const { brandId, contractId, clarificationId, reply } = payload;
    try {
        const response = yield brandOpsApi(
            brandId,
            brandOpsContractClarificationReplyUrl(contractId, clarificationId),
            httpRequest.POST,
            { reply },
        );
        const data = unwrapData(response);
        yield put(replyToContractClarificationSuccess(data as any));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(replyToContractClarificationFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* brandOpsSaga() {
    yield all([
        takeLatest(fetchBrandOpsAccess as any, fetchAccessSaga),
        takeLatest(fetchBrandOpsTeam as any, fetchTeamSaga),
        takeLatest(inviteBrandOpsTeam as any, inviteTeamSaga),
        takeLatest(updateBrandOpsTeamMember as any, updateTeamMemberSaga),
        takeLatest(removeBrandOpsTeamMember as any, removeTeamMemberSaga),
        takeLatest(acceptBrandOpsInvite as any, acceptInviteSaga),
        takeLatest(fetchBrandOpsContracts as any, fetchContractsSaga),
        takeLatest(createBrandOpsContract as any, createContractSaga),
        takeLatest(getBrandOpsContract as any, getContractSaga),
        takeLatest(dispatchBrandOpsContract as any, dispatchContractSaga),
        takeLatest(signBrandOpsContract as any, signContractSaga),
        takeLatest(downloadBrandOpsContractPdf as any, downloadContractPdfSaga),
        takeLatest(fetchBrandOpsCalendar as any, fetchCalendarSaga),
        takeLatest(createBrandOpsCalendarItem as any, createCalendarSaga),
        takeLatest(updateBrandOpsCalendarItem as any, updateCalendarSaga),
        takeLatest(approveBrandOpsCalendarItem as any, approveCalendarSaga),
        takeLatest(publishBrandOpsCalendarItem as any, publishCalendarSaga),
        takeLatest(fetchBrandOpsHomeStats as any, fetchHomeStatsSaga),
        takeLatest(fetchBrandOpsSettings as any, fetchSettingsSaga),
        takeLatest(updateBrandOpsSettings as any, updateSettingsSaga),
        takeLatest(fetchBrandOpsKnowledge as any, fetchKnowledgeSaga),
        takeLatest(addBrandOpsKnowledge as any, addKnowledgeSaga),
        takeLatest(removeBrandOpsKnowledge as any, removeKnowledgeSaga),
        takeLatest(runBrandOpsAiPlayground as any, aiPlaygroundSaga),
        takeLatest(fetchBrandOpsInbox as any, fetchInboxSaga),
        takeLatest(fetchBrandOpsInboxStatus as any, fetchInboxStatusSaga),
        takeLatest(lockBrandOpsEscrow as any, lockEscrowSaga),
        takeLatest(payoutBrandOpsEscrow as any, payoutEscrowSaga),
        takeLatest(releaseBrandOpsEscrow as any, releaseEscrowSaga),
        takeLatest(addBrandOpsStrike as any, addStrikeSaga),
        takeLatest(fetchBrandOpsConversations as any, fetchConversationsSaga),
        takeLatest(fetchBrandOpsConversationMessages as any, fetchConversationMessagesSaga),
        takeLatest(sendBrandOpsInboxMessage as any, sendInboxMessageSaga),
        takeLatest(fetchBrandOpsUgcAssets as any, fetchUgcAssetsSaga),
        takeLatest(fetchMyBrandMemberships as any, fetchMyMembershipsSaga),
        takeLatest(fetchMyBrandOpsContract as any, fetchMyContractSaga),
        takeLatest(resendBrandOpsTeamInvite as any, resendTeamInviteSaga),
        takeLatest(renewBrandOpsContract as any, renewContractSaga),
        takeLatest(freezeBrandOpsContract as any, freezeContractSaga),
        takeLatest(voidBrandOpsContract as any, voidContractSaga),
        takeLatest(fetchBrandOpsAutomations as any, fetchAutomationsSaga),
        takeLatest(upsertBrandOpsAutomation as any, upsertAutomationSaga),
        takeLatest(toggleBrandOpsAutomation as any, toggleAutomationSaga),
        takeLatest(approveBrandOpsContract as any, approveContractSaga),
        takeLatest(getContractForReview as any, getContractForReviewSaga),
        takeLatest(fetchContractClarifications as any, fetchContractClarificationsSaga),
        takeLatest(replyToContractClarification as any, replyToContractClarificationSaga),
    ]);
}
